import { c as _c } from "react/compiler-runtime";
// One chat thread (hq.chat): messages oldest first, reactions, a composer. Used in the task sheet/page (the thread
// about the task, made by the first message) and as the Chats page's thread panel. The conversation is the core's
// (chat/core: chat.conversation), kept current: a send shows at once (muted, `pending`) and the core rolls it back if
// refused, then the text comes back here. Marks the thread read when shown (up to its newest message). Files sent with
// a message (attach, or drop them on the thread) are its attachments (hq.files 1.1.0): shown under it, opened on click;
// an agent the message addresses gets them as files.
// Styles: platform.css `.thread`.
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type Attachment, api, local, type MdDoc, type Message } from "@/api";
import { useLoad } from "@/lib/load";
import { errorText, useAuth } from "@/store";
import { Empty } from "./empty";
import { ErrorLine } from "./error-line";
import { Initials } from "./initials";
import { LinkButton } from "./link-button";
import { Loader } from "./loader";
import { Markdown } from "./markdown";
import { SectionHead } from "./section-head";
const REACTIONS = ["👍", "🔥", "✅", "👀"];
const kb = (n: number) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
const docs = new Map<string, Promise<MdDoc | null>>();
/** A message's text: its markdown drawn (links open: an app path in the app, others in a new tab) once the core has
 *  parsed it, plain until then. */
function Text(t0) {
  const $ = _c(8);
  const {
    body
  } = t0;
  const [doc, setDoc] = useState(null);
  let t1;
  let t2;
  if ($[0] !== body) {
    t1 = () => {
      let live = true;
      if (!docs.has(body)) {
        docs.set(body, api.chat.markdown(body).catch(_temp));
      }
      docs.get(body).then(d => live && setDoc({
        body,
        doc: d
      }));
      return () => {
        live = false;
      };
    };
    t2 = [body];
    $[0] = body;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const d_0 = doc?.body === body ? doc.doc : null;
  let t3;
  if ($[3] !== body || $[4] !== d_0) {
    t3 = d_0 ? <Markdown doc={d_0} /> : body;
    $[3] = body;
    $[4] = d_0;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="text">{t3}</div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function _temp() {
  return null;
}
const time = (iso: string | null) => (iso ? new Date(iso) : new Date()).toLocaleTimeString([], {
  hour: "2-digit",
  minute: "2-digit"
});
type Props = /** an existing thread */
{
  thread: string;
  about?: never;
  space?: never;
  title?: string;
  head?: boolean;
}
/** the thread about a record (created by the first message) */ | {
  about: string;
  space?: string | null;
  thread?: never;
  title?: string;
  head?: boolean;
};
export function ThreadView(props: Props) {
  const {
    t
  } = useTranslation();
  const me = useAuth(s => s.me);
  const key = props.thread ?? props.about ?? "";
  const [conv, setConv] = useState<{
    key: string;
    thread: string | null;
    messages: Message[] | null;
    error: string | null;
  }>({
    key,
    thread: null,
    messages: null,
    error: null
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the thread or the record
  useEffect(() => api.chat.conversation(props.thread ? {
    thread: props.thread
  } : {
    about: props.about!
  }, (c, error) => setConv({
    key,
    thread: c.thread,
    messages: c.messages,
    error
  })), [key]);
  const current = conv.key === key ? conv : null;
  const threadId = current?.thread ?? props.thread ?? null;
  const list = current?.messages ?? [];
  const msgs = {
    loading: !current?.messages && !current?.error,
    busy: !current?.messages,
    error: current?.error ?? null
  };
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [dropping, setDropping] = useState(false);
  const pick = useRef<HTMLInputElement>(null);
  const [error_0, setError] = useState<string | null>(null);
  const attached = useLoad(() => threadId ? api.chat.attachments(threadId) : Promise.resolve({} as Record<string, Attachment[]>), `chat.attachments:${threadId}`, {
    kinds: ["Version"]
  });
  const last = list.filter(m => !m.pending).at(-1)?.id;
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (threadId && last) void api.chat.read(threadId, last).catch(() => {});
  }, [threadId, last]);
  // follow new messages once the thread is shown (not on the first load: the page keeps its scroll)
  const seen = useRef(-1);
  useEffect(() => {
    if (seen.current >= 0 && list.length > seen.current) endRef.current?.scrollIntoView({
      block: "nearest",
      behavior: "smooth"
    });
    if (current?.messages) seen.current = list.length;
  }, [list.length, current?.messages]);
  const send = async () => {
    const sending = files;
    // files alone: the message names them
    const body = text.trim() || sending.map(f => `\u{1F4CE} ${f.name}`).join("\n");
    if (!body) return;
    setText("");
    setFiles([]);
    setError(null);
    try {
      const m_0 = await api.chat.send(threadId ? {
        thread: threadId
      } : {
        about: props.about!,
        space: props.space ?? null,
        title: props.title
      }, body);
      if (sending.length) {
        await api.chat.attach(m_0.id, sending);
        attached.reload();
      }
    } catch (e) {
      setText(text);
      setFiles(sending);
      setError(errorText(e));
    }
  };
  const open = async (a: Attachment) => {
    try {
      window.open(await api.blobs.open(a.blob), "_blank");
    } catch (e_0) {
      setError(errorText(e_0));
    }
  };
  const react = async (m_1: Message, emoji: string) => {
    try {
      await api.chat.react(m_1.id, emoji);
    } catch (e_1) {
      setError(errorText(e_1));
    }
  };
  return <section className={dropping ? "thread dropping" : "thread"} data-t="thread" data-thread={threadId ?? undefined} aria-label={t("chat.thread")} onDragOver={e_2 => {
    if (!e_2.dataTransfer.types.includes("Files")) return;
    e_2.preventDefault();
    setDropping(true);
  }} onDragLeave={() => setDropping(false)} onDrop={e_3 => {
    if (!e_3.dataTransfer.files.length) return;
    e_3.preventDefault();
    setDropping(false);
    setFiles(fs => [...fs, ...e_3.dataTransfer.files]);
  }}>
			{props.head !== false && <SectionHead>{t("chat.thread")}</SectionHead>}
			<Loader on={msgs.loading} />
			{/* its own page (Chats): the full empty state; inside a task's detail: one line */}
			{list.length === 0 && !msgs.busy && (props.head === false ? <Empty icon="chats" title={t("chat.noMessages")} text={t("chat.emptyText")} /> : <div className="muted note">{t("chat.empty")}</div>)}
			<ul className="messages">
				{list.map((m_2, i) => {
        const cont = list[i - 1]?.by.id === m_2.by.id;
        return <li key={m_2.id} className={[m_2.pending && "pending", cont && "cont", m_2.by.id === me?.id && "mine"].filter(Boolean).join(" ")} data-t="message" data-id={m_2.id} tabIndex={-1}>
							{/* the react bar: floated at the message's top right, so its first line wraps around it (never under it) */}
							{!m_2.pending && <span className="add">
									{REACTIONS.filter(e_4 => !m_2.reactions.some(r => r.emoji === e_4)).map(e_5 => <button key={e_5} type="button" className="reaction ghost" aria-label={`${t("chat.react")} ${e_5}`} onClick={() => void react(m_2, e_5)}>
											{e_5}
										</button>)}
								</span>}
							{!cont && <div className="by">
									<Initials name={m_2.by.name} />
									<span className="name">{m_2.by.name}</span>
									<span className="mono muted">{time(m_2.at)}</span>
								</div>}
							<Text body={m_2.body} />
							{(attached.data?.[m_2.id]?.length ?? 0) > 0 && <div className="files" data-t="attachments">
									{attached.data![m_2.id]!.map(a_0 => <button key={a_0.id} type="button" className="file-chip" data-t="attachment" title={a_0.contentType} onClick={() => void open(a_0)}>
											{"\u{1F4CE}"} {a_0.name} <span className="mono muted">{kb(a_0.size)}</span>
										</button>)}
								</div>}
							{m_2.reactions.length > 0 && <div className="reactions">
									{m_2.reactions.map(r_0 => <button key={r_0.emoji} type="button" className="reaction" aria-pressed={r_0.mine} onClick={() => void react(m_2, r_0.emoji)}>
											{r_0.emoji} <span className="mono">{r_0.count}</span>
										</button>)}
								</div>}
						</li>;
      })}
			</ul>
			<div ref={endRef} />
			{files.length > 0 && <div className="files composer-files" data-t="composer-files">
					{files.map((f_0, i_0) => <button key={`${f_0.name}:${i_0}`} type="button" className="file-chip" aria-label={`${t("chat.unattach")} ${f_0.name}`} onClick={() => setFiles(fs_0 => fs_0.filter((_, j) => j !== i_0))}>
							{"\u{1F4CE}"} {f_0.name} <span className="mono muted">{kb(f_0.size)}</span> {"\u00d7"}
						</button>)}
				</div>}
			<form className="composer" onSubmit={e_6 => {
      e_6.preventDefault();
      void send();
    }}>
				<textarea data-t="composer" rows={1} placeholder={t("chat.placeholder")} aria-label={t("chat.placeholder")} value={text} onChange={e_7 => setText(e_7.target.value)} onKeyDown={e_8 => {
        if (e_8.key === "Enter" && !e_8.shiftKey) {
          e_8.preventDefault();
          void send();
        }
      }} />
				<input ref={pick} type="file" multiple hidden data-t="attach-input" onChange={e_9 => {
        const picked = [...(e_9.target.files ?? [])];
        e_9.target.value = "";
        setFiles(fs_1 => [...fs_1, ...picked]);
      }} />
				<LinkButton data-t="attach" aria-label={t("chat.attach")} title={t("chat.attach")} onClick={() => pick.current?.click()}>
					{"\u{1F4CE}"}
				</LinkButton>
				<LinkButton type="submit" data-t="send" disabled={!text.trim() && !files.length}>
					{t("chat.send")}
				</LinkButton>
			</form>
			{(error_0 || msgs.error) && <ErrorLine>{error_0 ?? msgs.error}</ErrorLine>}
			{threadId && <div className="muted mono note">{local(threadId)}</div>}
		</section>;
}